import { useState } from 'react';
import { DsButton, DsInput, DsModal } from '@/modules/internal-v2/ds';
import {
  useMerge,
  usePickerProductLines,
  usePickerProductModels,
  usePickerVocabulary,
} from '@/api/specifications/specifications.hooks';
import type {
  MergePreview,
  ResourceType,
} from '@/api/specifications/specifications.types';

interface MergeModalProps {
  open: boolean;
  resource: ResourceType;
  sourceId: number;
  sourceLabel: string;
  /** Pickers scope — required to filter merge targets correctly. */
  scope: {
    manufacturerId?: number;
    category?: string;
    productLineId?: number;
    attribute?: string;
  };
  onClose: () => void;
}

export function MergeModal({
  open,
  resource,
  sourceId,
  sourceLabel,
  scope,
  onClose,
}: MergeModalProps) {
  const [search, setSearch] = useState('');
  const [targetId, setTargetId] = useState<number | null>(null);
  const [preview, setPreview] = useState<MergePreview | null>(null);

  const merge = useMerge();

  const linesQuery = usePickerProductLines({
    manufacturerId: scope.manufacturerId,
    category: scope.category,
    state: 'approved',
    search: search || undefined,
  });
  const modelsQuery = usePickerProductModels({
    productLineId: scope.productLineId,
    state: 'approved',
    search: search || undefined,
  });
  const vocabQuery = usePickerVocabulary({
    manufacturerId: scope.manufacturerId,
    attribute: scope.attribute,
    state: 'approved',
    search: search || undefined,
  });

  const candidates =
    resource === 'productLine'
      ? linesQuery.data
      : resource === 'model'
        ? modelsQuery.data
        : vocabQuery.data;

  const handleDryRun = async (id: number) => {
    setTargetId(id);
    const res = await merge.mutateAsync({
      resource,
      id: sourceId,
      body: { targetId: id, dryRun: true },
    });
    setPreview(res.preview);
  };

  const handleConfirm = async () => {
    if (targetId == null) return;
    await merge.mutateAsync({
      resource,
      id: sourceId,
      body: { targetId, dryRun: false },
    });
    onClose();
  };

  return (
    <DsModal
      open={open}
      onClose={onClose}
      title={`Merge ${resource}: ${sourceLabel}`}
      description="Choose an approved target. A dry-run preview will show what will change before you confirm."
      footer={
        <>
          <DsButton variant="ghost" onClick={onClose}>
            Cancel
          </DsButton>
          <DsButton
            variant="primary"
            onClick={handleConfirm}
            disabled={!preview || merge.isPending}
          >
            Confirm merge
          </DsButton>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <DsInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search approved targets…"
        />
        <div
          style={{
            maxHeight: 200,
            overflowY: 'auto',
            border: '1px solid var(--border)',
          }}
        >
          {candidates?.map((c) => {
            const label = 'value' in c ? c.value : c.name;
            return (
              <div
                key={c.id}
                style={{
                  padding: 6,
                  cursor: 'pointer',
                  background:
                    targetId === c.id ? 'var(--surface-active)' : undefined,
                }}
                onClick={() => handleDryRun(c.id)}
              >
                {label}
              </div>
            );
          })}
          {candidates && candidates.length === 0 && (
            <div className="sp-empty">Keine freigegebenen Ziele</div>
          )}
        </div>
        {preview && (
          <div className="sp-merge-preview">
            {JSON.stringify(preview, null, 2)}
          </div>
        )}
      </div>
    </DsModal>
  );
}
